import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  Library as LibraryIcon,
  Lock,
  LogIn,
  Play,
  SearchX,
  Users,
} from "lucide-react";
import { Header } from "@/components/Header";
import { LibraryCreatorFilter } from "@/components/LibraryCreatorFilter";
import { LibraryNextDropBanner } from "@/components/LibraryNextDropBanner";
import { LibraryWatchFilter } from "@/components/LibraryWatchFilter";
import { VideoThumbnailProgress } from "@/components/VideoThumbnailProgress";
import {
  highlightSearchExcerpt,
  keywordReasonTokens,
  LibrarySearchBar,
  matchReasonLabel,
  type LibrarySearchMatch,
  useLibrarySearch,
  visibleMatchReasons,
} from "@/components/LibrarySearch";
import { type WeeklyDrop, useUserActivity } from "@/lib/firebase";
import { useAuth } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import { useLibraryNextDrop } from "@/lib/use-library-next-drop";
import {
  isLibraryOnlyMember,
} from "@/lib/membership-tier";
import { legacyCatalogScopeLabel } from "@/lib/legacy-membership";
import { filterDropsByCreators, uniqueLibraryCreators } from "@/lib/library-creators";
import { filterDropsByWatchStatus, type LibraryWatchStatus } from "@/lib/library-watch-filter";
import { getWatchTimeSeconds } from "@/lib/watch-progress";
import { useLibraryPage } from "@/lib/use-library-page";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/library")({
  head: () => ({
    meta: [
      { title: "My Library — Real Pranks" },
      {
        name: "description",
        content:
          "Your personal library of every weekly drop you've unlocked. Yours forever as long as you stay subscribed.",
      },
      { property: "og:title", content: "My Library — Real Pranks" },
      {
        property: "og:description",
        content: "Every drop you own, in one place.",
      },
    ],
  }),
  component: LibraryPage,
});

function formatReleaseDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function LegacyLibraryBanner({ compact = false }: { compact?: boolean }) {
  const { creators } = useAuth();
  const scopeLabel = legacyCatalogScopeLabel(creators);

  return (
    <div
      className={
        compact
          ? "mt-8 rounded-2xl border border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5 p-5 sm:p-6"
          : "mt-8 rounded-2xl border border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5 p-5 sm:p-6"
      }
    >
      <h2 className="text-base font-black tracking-tight sm:text-lg">
        You&apos;re an OG Legacy member — full catalog access
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        As an OG Legacy member, you have full access to {scopeLabel}. New releases in
        your membership scope are automatically unlocked while you stay subscribed.
      </p>
    </div>
  );
}

function LibraryPage() {
  const {
    allDrops,
    canReadLibrary,
    hasSubscription,
    owned,
    missed,
    fullVault,
    isLegacyMember,
    showLoading,
    isRefreshing,
  } = useLibraryPage();

  if (showLoading) {
    return (
      <PageShell ownedCount={0} missedCount={0} hideStats>
        {isLegacyMember && <LegacyLibraryBanner compact />}
        <p className="mt-10 text-sm text-muted-foreground">Loading your library...</p>
      </PageShell>
    );
  }

  if (!canReadLibrary) {
    return (
      <PageShell ownedCount={0} missedCount={0} hideStats>
        <div className="mt-16 rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <LogIn className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-4 text-2xl font-black tracking-tight">Sign in to see your library.</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Your library follows your account. Sign in to view every drop you own.
          </p>
          <Link
            to="/account"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-bold tracking-[0.2em] text-background uppercase"
          >
            Sign In
          </Link>
        </div>
      </PageShell>
    );
  }

  if (!hasSubscription) {
    const released = allDrops.filter((d) => d.status !== "locked");
    const previewDrops = released.slice(-3).reverse();
    return (
      <PageShell ownedCount={0} missedCount={0} hideStats>
        <div className="mt-10 rounded-2xl border border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5 p-6 text-center sm:p-8">
          <Lock className="mx-auto h-7 w-7 text-[oklch(0.62_0.19_250)]" />
          <h2 className="mt-3 text-xl font-black tracking-tight sm:text-2xl">
            3 drops waiting for you.
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Subscribe to unlock these drops and keep receiving a new one every week.
          </p>
          <Link
            to="/membership"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[oklch(0.62_0.19_250)] px-6 py-3 text-xs font-bold tracking-[0.2em] text-white uppercase"
          >
            Subscribe to Access
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {previewDrops.map((d, i) => (
            <LockedCard key={`locked-${d.seriesId}-${d.week}-${d.slug}`} drop={d} weeksAgo={i} />
          ))}
        </div>
      </PageShell>
    );
  }

  return (
    <SubscribedLibrary
      owned={owned}
      missed={missed}
      fullVault={fullVault}
      isLegacyMember={isLegacyMember}
      isRefreshing={isRefreshing}
    />
  );
}

function SubscribedLibrary({
  owned,
  missed,
  fullVault,
  isLegacyMember,
  isRefreshing,
}: {
  owned: WeeklyDrop[];
  missed: WeeklyDrop[];
  fullVault: boolean;
  isLegacyMember: boolean;
  isRefreshing: boolean;
}) {
  const dev = useDevState();
  const { subscriptions } = useAuth();
  const isLibraryOnly = isLibraryOnlyMember(subscriptions, {
    enabled: dev.state !== "real" && dev.isSubscribed,
    tier: dev.tier,
  });
  const { activity } = useUserActivity();
  const showNextDropBanner = !fullVault && !isLegacyMember;
  const { nextDrop, nextReleaseAt, pending, showBanner } = useLibraryNextDrop(
    showNextDropBanner,
  );
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"quick" | "smart">("quick");
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([]);
  const [watchStatus, setWatchStatus] = useState<LibraryWatchStatus>("all");

  const libraryCreators = useMemo(() => uniqueLibraryCreators(owned), [owned]);
  const showCreatorFilter = libraryCreators.length > 1;

  const creatorFilteredOwned = useMemo(
    () => filterDropsByCreators(owned, selectedCreatorIds),
    [owned, selectedCreatorIds],
  );

  const watchFilteredOwned = useMemo(
    () => filterDropsByWatchStatus(creatorFilteredOwned, activity, watchStatus),
    [creatorFilteredOwned, activity, watchStatus],
  );

  useEffect(() => {
    const validIds = new Set(libraryCreators.map((creator) => creator.id));
    const next = selectedCreatorIds.filter((id) => validIds.has(id));
    if (next.length !== selectedCreatorIds.length) {
      setSelectedCreatorIds(next);
    }
  }, [libraryCreators, selectedCreatorIds]);

  const {
    drops: filtered,
    matches,
    isSearching,
    isLoadingSmart,
    smartError,
    didAutoSwitchToSmart,
  } = useLibrarySearch(watchFilteredOwned, query, mode, setMode, {
    useVectorBackend: dev.state === "real",
  });

  const showCreatorEmpty =
    selectedCreatorIds.length > 0 && !isSearching && creatorFilteredOwned.length === 0;

  const showWatchEmpty =
    watchStatus === "unwatched" &&
    !isSearching &&
    watchFilteredOwned.length === 0 &&
    creatorFilteredOwned.length > 0;

  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchOpen = searchExpanded || query.trim().length > 0;

  return (
    <PageShell
      ownedCount={owned.length}
      missedCount={missed.length}
      isRefreshing={isRefreshing}
      headerAddon={
        owned.length > 0 ? (
          <div className="flex w-full flex-wrap items-center gap-2 overflow-visible py-0.5">
            <div className={cn(searchOpen && "w-full max-w-2xl basis-full")}>
              <LibrarySearchBar
                variant="header"
                expanded={searchExpanded}
                onExpandedChange={setSearchExpanded}
                query={query}
                setQuery={setQuery}
                mode={mode}
                setMode={setMode}
                resultCount={filtered.length}
                totalCount={watchFilteredOwned.length}
                isLoadingSmart={isLoadingSmart}
                didAutoSwitchToSmart={didAutoSwitchToSmart}
              />
            </div>
            <LibraryWatchFilter
              className={searchOpen ? "w-full basis-full" : undefined}
              layout={searchOpen ? "stacked" : "inline"}
              drops={creatorFilteredOwned}
              activity={activity}
              watchStatus={watchStatus}
              onWatchStatusChange={setWatchStatus}
            />
            {showCreatorFilter && (
              <LibraryCreatorFilter
                className={searchOpen ? "w-full basis-full" : undefined}
                layout={searchOpen ? "stacked" : "inline"}
                creators={libraryCreators}
                selectedCreatorIds={selectedCreatorIds}
                onSelectedCreatorIdsChange={setSelectedCreatorIds}
                totalCount={owned.length}
              />
            )}
          </div>
        ) : undefined
      }
    >
      {showBanner && !isSearching && (
        <LibraryNextDropBanner
          nextDrop={nextDrop}
          nextReleaseAt={nextReleaseAt}
          pending={pending}
          isLibraryOnly={isLibraryOnly}
        />
      )}

      {fullVault && isLegacyMember && <LegacyLibraryBanner />}

      {fullVault && !isLegacyMember && (
        <div className="mt-8 rounded-2xl border border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5 p-5 sm:p-6">
          <h2 className="text-base font-black tracking-tight sm:text-lg">
            You have access to every video in the vault.
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Stay subscribed to receive new videos when they&apos;re released by the creators.
          </p>
        </div>
      )}

      {!isSearching && missed.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xs font-bold tracking-[0.25em] text-muted-foreground uppercase">
            Missed Drops
          </h2>
          <p className="mt-1 text-sm text-muted-foreground/80">
            Released while your subscription was inactive — gone for good.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {missed.map((d) => (
              <div
                key={`missed-${d.seriesId}-${d.week}-${d.slug}`}
                className="relative aspect-video overflow-hidden rounded-lg bg-card opacity-50 ring-1 ring-border"
              >
                <img
                  src={d.thumbnail ?? ""}
                  alt={d.title}
                  loading="lazy"
                  className="h-full w-full scale-110 object-cover blur-md grayscale"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {showCreatorEmpty ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <Users className="mx-auto h-7 w-7 text-muted-foreground" />
          <h3 className="mt-3 text-base font-bold">No videos for selected creators</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Try selecting All creators or pick different creators.
          </p>
        </div>
      ) : showWatchEmpty ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <CheckCircle2 className="mx-auto h-7 w-7 text-muted-foreground" />
          <h3 className="mt-3 text-base font-bold">You&apos;ve started every video here</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Switch to All to see your full library, including videos in progress.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <SearchX className="mx-auto h-7 w-7 text-muted-foreground" />
          <h3 className="mt-3 text-base font-bold">No matches for "{query}"</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {isLoadingSmart
              ? "Searching library…"
              : smartError
                ? smartError
                : didAutoSwitchToSmart
                  ? "No quick matches — Smart search checked your library too."
                  : mode === "quick"
                    ? "Try Smart search for related ideas, or check your spelling."
                    : "Try different keywords or switch to Quick search."}
          </p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => (
            <LibraryCard
              key={`${d.seriesId}-${d.week}-${d.slug}`}
              drop={d}
              watchTimeSeconds={getWatchTimeSeconds(activity, d.video.id)}
              searchMatch={isSearching ? matches[d.video.id] : undefined}
              searchQuery={isSearching ? query : undefined}
            />
          ))}
        </div>
      )}
    </PageShell>
  );
}

function PageShell({
  children,
  ownedCount,
  missedCount,
  hideStats = false,
  isRefreshing = false,
  headerAddon,
}: {
  children: React.ReactNode;
  ownedCount: number;
  missedCount: number;
  hideStats?: boolean;
  isRefreshing?: boolean;
  headerAddon?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto w-full max-w-7xl px-4 py-12 sm:py-16">
        <div className="flex flex-col gap-4 overflow-visible border-b border-border pb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase ring-1 ring-border">
                  <LibraryIcon className="h-3 w-3" />
                  Your Library
                </span>
                <span
                  aria-live="polite"
                  aria-hidden={!isRefreshing}
                  className={cn(
                    "text-[10px] font-medium tracking-normal text-muted-foreground normal-case transition-opacity duration-200",
                    isRefreshing ? "opacity-100" : "opacity-0",
                  )}
                >
                  Updating…
                </span>
              </div>
              <h1 className="mt-4 text-balance text-4xl font-black tracking-tight sm:text-5xl">
                Your Video Library
              </h1>
            </div>
            {!hideStats && (
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <div className="text-3xl font-black tabular-nums text-foreground">
                    {ownedCount}
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">
                    In Library
                  </div>
                </div>
                {missedCount > 0 && (
                  <div>
                    <div className="text-3xl font-black tabular-nums text-muted-foreground/60">
                      {missedCount}
                    </div>
                    <div className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">
                      Missed
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {headerAddon}
        </div>
        {children}
      </main>
    </div>
  );
}

function LockedCard({ drop, weeksAgo }: { drop: WeeklyDrop; weeksAgo: number }) {
  const isCurrent = weeksAgo === 0;
  const label =
    weeksAgo === 0 ? "This Week" : weeksAgo === 1 ? "Last Week" : `${weeksAgo} Weeks Ago`;
  return (
    <div
      className={cn(
        "group relative aspect-video overflow-hidden rounded-xl bg-card ring-1 ring-border",
        isCurrent && "ring-[oklch(0.62_0.19_250)]/60",
      )}
    >
      <img
        src={drop.thumbnail ?? ""}
        alt={drop.title}
        loading="lazy"
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background/95 text-foreground shadow-2xl ring-1 ring-border">
          <Lock className="h-4 w-4" />
        </span>
      </div>
      <span
        className={cn(
          "absolute top-2 left-2 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase",
          isCurrent
            ? "bg-[oklch(0.62_0.19_250)] text-white"
            : "bg-background/85 text-foreground ring-1 ring-border",
        )}
      >
        {label}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-3">
        <h3 className="line-clamp-1 text-sm font-bold text-white drop-shadow">{drop.title}</h3>
        <p className="mt-0.5 text-[10px] font-bold tracking-[0.2em] text-white/70 uppercase">
          {formatReleaseDate(drop.releaseAt)}
        </p>
      </div>
    </div>
  );
}

function LibraryCard({
  drop,
  watchTimeSeconds,
  searchMatch,
  searchQuery,
}: {
  drop: WeeklyDrop;
  watchTimeSeconds: number;
  searchMatch?: LibrarySearchMatch;
  searchQuery?: string;
}) {
  const isCurrent = drop.status === "current";
  const isNew =
    drop.tag === "NEW" &&
    !!drop.video?.publishedAtIso &&
    new Date(drop.video.publishedAtIso).getTime() > Date.UTC(2026, 4, 20);
  const visibleReasons = searchMatch ? visibleMatchReasons(searchMatch.reasons) : [];
  const showMatch =
    Boolean(searchQuery) &&
    visibleReasons.some((reason) => reason.excerpt || reason.startSec != null);

  return (
    <Link
      to="/video/$slug"
      params={{ slug: drop.slug }}
      className={cn(
        "group relative block overflow-hidden rounded-xl bg-card ring-1 ring-border transition-all duration-300 hover:ring-foreground/40",
        isCurrent && "ring-[oklch(0.62_0.19_250)] shadow-[0_0_30px_-10px_oklch(0.62_0.19_250/0.6)]",
      )}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={drop.thumbnail ?? ""}
          alt={drop.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {isNew && (
          <span className="absolute top-2 left-2 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase ring-1 backdrop-blur bg-primary text-primary-foreground ring-primary/30">
            New
          </span>
        )}

        <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white tabular-nums backdrop-blur">
          {drop.duration}
        </span>

        <VideoThumbnailProgress
          watchTimeSeconds={watchTimeSeconds}
          durationSeconds={drop.video.duration}
        />

        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background/95 text-foreground shadow-2xl">
            <Play className="h-5 w-5 fill-current" strokeWidth={2.5} />
          </span>
        </div>
      </div>

      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">
            {formatReleaseDate(drop.releaseAt)}
          </div>
          <h3 className="mt-1 line-clamp-2 text-sm font-bold text-foreground">{drop.title}</h3>
          {showMatch && searchQuery && (
            <div className="mt-2 space-y-0">
              {visibleReasons.map((reason, i) => (
                <div
                  key={`${reason.source}-${reason.matchedField ?? "transcript"}-${reason.startSec ?? i}`}
                  className={cn(i > 0 && "mt-2 border-t border-border/60 pt-2")}
                >
                  {reason.matchedField === "keywords" ? (
                    <div className="flex flex-wrap gap-1.5">
                      {keywordReasonTokens(reason).map((keyword) => (
                        <span
                          key={keyword}
                          className="rounded-md border border-border px-2 py-0.5 text-xs text-foreground"
                        >
                          {highlightSearchExcerpt(keyword, searchQuery)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className="text-[9px] font-bold tracking-[0.15em] text-muted-foreground uppercase">
                        {matchReasonLabel(reason)}
                      </p>
                      {reason.excerpt ? (
                        <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                          {highlightSearchExcerpt(reason.excerpt, searchQuery)}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">{drop.video.creator.name}</p>
        </div>
        {isCurrent && (
          <span className="flex-shrink-0 rounded-full bg-[oklch(0.62_0.19_250)]/15 px-2 py-0.5 text-[9px] font-bold tracking-[0.2em] text-[oklch(0.62_0.19_250)] uppercase">
            Now
          </span>
        )}
      </div>
    </Link>
  );
}
